/**
 * Fechas civiles (`YYYY-MM-DD`) y meses (`YYYY-MM`) **como texto**, con aritmética de calendario sobre el número
 * de días desde 1970-01-01 (algoritmo `days_from_civil` de H. Hinnant). Aquí no se usa `Date`: «hoy» en
 * America/Bogota lo da `todayCivil` de `format.ts`, el único sitio que convierte instantes.
 */

const pad = (value: number, length = 2) => String(value).padStart(length, '0');

const MONTH_NAMES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'] as const;
/** Lunes primero, como la semana del calendario. */
export const DAY_NAMES = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'] as const;
export const DAY_SHORT = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'] as const;

function parseCivil(date: string): [number, number, number] {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) throw new RangeError(`Fecha civil no válida: ${date}`);
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

function parseMonth(month: string): [number, number] {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match || Number(match[2]) < 1 || Number(match[2]) > 12) throw new RangeError(`Mes no válido: ${month}`);
  return [Number(match[1]), Number(match[2])];
}

/** Días desde 1970-01-01 (proléptico gregoriano). */
function daysFromCivil(year: number, month: number, day: number): number {
  const y = month <= 2 ? year - 1 : year;
  const era = Math.floor(y / 400);
  const yearOfEra = y - era * 400;
  const dayOfYear = Math.floor((153 * (month + (month > 2 ? -3 : 9)) + 2) / 5) + day - 1;
  const dayOfEra = yearOfEra * 365 + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100) + dayOfYear;
  return era * 146_097 + dayOfEra - 719_468;
}

function civilFromDays(days: number): string {
  const z = days + 719_468;
  const era = Math.floor(z / 146_097);
  const dayOfEra = z - era * 146_097;
  const yearOfEra = Math.floor((dayOfEra - Math.floor(dayOfEra / 1460) + Math.floor(dayOfEra / 36_524) - Math.floor(dayOfEra / 146_096)) / 365);
  const dayOfYear = dayOfEra - (365 * yearOfEra + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100));
  const mp = Math.floor((5 * dayOfYear + 2) / 153);
  const day = dayOfYear - Math.floor((153 * mp + 2) / 5) + 1;
  const month = mp < 10 ? mp + 3 : mp - 9;
  const year = yearOfEra + era * 400 + (month <= 2 ? 1 : 0);
  return `${pad(year, 4)}-${pad(month)}-${pad(day)}`;
}

/** Día de la semana con el lunes como 0 (1970-01-01 fue jueves). */
function dayOfWeekFromDays(days: number): number {
  return (((days + 3) % 7) + 7) % 7;
}

export function dayOfWeekIndex(date: string): number {
  return dayOfWeekFromDays(daysFromCivil(...parseCivil(date)));
}

/** `2030-03-15` → `2030-03`. */
export function monthOf(date: string): string {
  parseCivil(date);
  return date.slice(0, 7);
}

/** Suma (o resta) meses a un mes `YYYY-MM`. */
export function addMonths(month: string, amount: number): string {
  const [year, number] = parseMonth(month);
  const index = year * 12 + (number - 1) + amount;
  return `${pad(Math.floor(index / 12), 4)}-${pad((index % 12) + 1)}`;
}

function daysInMonth(year: number, month: number): number {
  return daysFromCivil(month === 12 ? year + 1 : year, month === 12 ? 1 : month + 1, 1) - daysFromCivil(year, month, 1);
}

/** Lunes de la semana de una fecha. */
export function mondayOf(date: string): string {
  const days = daysFromCivil(...parseCivil(date));
  return civilFromDays(days - dayOfWeekFromDays(days));
}

/**
 * Días de la rejilla de un mes: del lunes de la semana del día 1 al domingo de la semana del último día,
 * incluidos los que asoman de los meses vecinos. Siempre semanas completas (28 a 42 días).
 */
export function monthGrid(month: string): string[] {
  const [year, number] = parseMonth(month);
  const first = daysFromCivil(year, number, 1);
  const last = first + daysInMonth(year, number) - 1;
  const start = first - dayOfWeekFromDays(first);
  const end = last + (6 - dayOfWeekFromDays(last));
  return Array.from({ length: end - start + 1 }, (_, index) => civilFromDays(start + index));
}

/** `2030-03` → `marzo de 2030`. */
export function monthTitle(month: string): string {
  const [year, number] = parseMonth(month);
  return `${MONTH_NAMES[number - 1]} de ${year}`;
}

/** `2030-03-05` → `martes, 5 de marzo de 2030`. */
export function longCivilDate(date: string): string {
  const [year, month, day] = parseCivil(date);
  return `${DAY_NAMES[dayOfWeekIndex(date)]}, ${day} de ${MONTH_NAMES[month - 1]} de ${year}`;
}

/** Número del día, sin ceros a la izquierda. */
export function dayNumber(date: string): number {
  return parseCivil(date)[2];
}
